// src/components/shared/useSectionedForm.js
// Drives AddFormLayout's section-nav state for a multi-section Add form.
//
// `sections` is a plain array: [{ id, label, fields: [...formFieldNames] }].
// `fields` must list every react-hook-form field name registered inside
// that section — it's how this hook maps a validation error back to the
// section that needs attention.
//
// For single-section forms, pass a one-element `sections` array — the nav
// rail in AddFormLayout hides itself automatically when there's only one.
import { useEffect, useMemo, useState } from "react";

export function useSectionedForm(sections, errors) {
  const [activeId, setActiveId] = useState(sections[0]?.id);

  const sectionsWithErrors = useMemo(
    () =>
      sections.map((s) => ({
        ...s,
        hasError: s.fields.some((f) => Boolean(errors?.[f])),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sections, errors]
  );

  // After a failed validation (Next-section check or final submit), if the
  // active section itself has no error but a different one does, jump
  // there — otherwise the user sees the click silently do nothing because
  // the actual invalid field is on a section they're not looking at.
  useEffect(() => {
    if (!errors || Object.keys(errors).length === 0) return;
    const active = sectionsWithErrors.find((s) => s.id === activeId);
    if (active?.hasError) return;
    const firstBad = sectionsWithErrors.find((s) => s.hasError);
    if (firstBad) setActiveId(firstBad.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  const activeIndex = sectionsWithErrors.findIndex((s) => s.id === activeId);
  const isFirst = activeIndex <= 0;
  const isLast = activeIndex === sectionsWithErrors.length - 1;

  const goNext = () => {
    if (!isLast) setActiveId(sectionsWithErrors[activeIndex + 1].id);
  };
  const goPrev = () => {
    if (!isFirst) setActiveId(sectionsWithErrors[activeIndex - 1].id);
  };

  return {
    activeId,
    setActiveId,
    sections: sectionsWithErrors,
    activeIndex,
    isFirst,
    isLast,
    goNext,
    goPrev,
  };
}
